import { ref as storageRef, uploadString, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { storage } from '@/firebase';

/**
 * Upload a base64 data URL to Firebase Storage and return a download URL.
 * Storage is ~8× cheaper per byte than RTDB, and images are cached by browsers
 * so they only download once rather than on every game state change.
 *
 * @param {string} dataUrl - base64 data URL from captureCanvas()
 * @param {string} path - storage path (e.g. "games/ROOM123/challenge/user456.jpg")
 * @returns {Promise<string|null>} download URL or null on failure
 */
export async function uploadGameImage(dataUrl, path) {
  if (!dataUrl || !path) return null;
  try {
    const ref = storageRef(storage, path);
    // uploadString handles the base64 → blob conversion internally
    await uploadString(ref, dataUrl, 'data_url');
    return await getDownloadURL(ref);
  } catch (err) {
    console.error('[uploadGameImage] Failed:', err);
    return null;
  }
}

/**
 * Delete a single image from Storage.
 */
export async function deleteGameImage(path) {
  if (!path) return;
  try {
    await deleteObject(storageRef(storage, path));
  } catch (err) {
    // 404 errors are fine — image already gone
    if (err?.code !== 'storage/object-not-found') {
      console.warn('[deleteGameImage] Failed:', err);
    }
  }
}

/**
 * Delete all game images for a room (called on game end).
 * Cleans up every path under games/{roomCode}/.
 */
export async function clearRoomGameImages(roomCode) {
  if (!roomCode) return;
  try {
    const folderRef = storageRef(storage, `games/${roomCode}`);
    const result = await listAll(folderRef);
    // Delete all top-level items + recurse into subfolders
    const deletions = [];
    for (const item of result.items) {
      deletions.push(deleteObject(item).catch(() => {}));
    }
    for (const prefix of result.prefixes) {
      deletions.push(clearStorageFolder(prefix));
    }
    await Promise.all(deletions);
  } catch (err) {
    console.warn('[clearRoomGameImages] Failed:', err);
  }
}

async function clearStorageFolder(folderRef) {
  try {
    const result = await listAll(folderRef);
    const deletions = [];
    for (const item of result.items) {
      deletions.push(deleteObject(item).catch(() => {}));
    }
    for (const prefix of result.prefixes) {
      deletions.push(clearStorageFolder(prefix));
    }
    await Promise.all(deletions);
  } catch {
    // Ignore — folder may already be empty
  }
}

/**
 * Build a standard image path for a game submission.
 */
export function buildSubmissionPath(roomCode, gameType, identifier) {
  return `games/${roomCode}/${gameType}/${identifier}_${Date.now()}.jpg`;
}
